import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { formatDateForInput } from '@/utils/dateUtils';
import { authFetch } from '@/services/api.service';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface CashRegisterStatus {
  isOpen: boolean;
  openingAmount: number | null;
  openedAt: string | null;
  openedBy: string | null;
  shift: string | null;
  actualShift: string | null;
  sessionId: string | null;
}

export interface UseCashRegisterStatusReturn {
  status: CashRegisterStatus;
  loading: boolean;
  error: string | null;
  checkStatus: () => Promise<void>;
}

export const useCashRegisterStatus = (): UseCashRegisterStatusReturn => {
  const { user, selectedLocation } = useAuth();
  const [status, setStatus] = useState<CashRegisterStatus>({
    isOpen: false,
    openingAmount: null,
    openedAt: null,
    openedBy: null,
    shift: null,
    actualShift: null,
    sessionId: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    const locationId = selectedLocation?.id || user?.locationId;
    if (!locationId) {
      setError('No hay ubicación seleccionada');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const today = formatDateForInput(new Date()); // Fixed: Use Peru timezone instead of UTC
      console.log('🔍 Checking cash register status for:', { locationId, today });

      const response = await authFetch(
        `${API_BASE_URL}/cash-register/${locationId}/${today}/status`
      );

      if (!response.ok) {
        // If status endpoint doesn't exist, check for open sessions
        const fallbackResponse = await authFetch(
          `${API_BASE_URL}/cash-register/${locationId}/${today}/all-shifts`
        );

        if (!fallbackResponse.ok) {
          throw new Error('Error al verificar estado de caja');
        }

        const fallbackData = await fallbackResponse.json();
        
        // Check if there's any open cash register session
        const openSessions = fallbackData.shifts?.filter((session: any) => !session.closedAt) || [];
        
        if (openSessions.length > 0) {
          const openSession = openSessions[0];
          setStatus({
            isOpen: true,
            openingAmount: parseFloat(openSession.openingAmount) || 0,
            openedAt: openSession.openedAt,
            openedBy: openSession.openedBy || 'N/A',
            shift: openSession.shift || 'N/A',
            actualShift: fallbackData.actualShift || null,
            sessionId: openSession.id
          });
        } else {
          setStatus({
            isOpen: false,
            openingAmount: null,
            openedAt: null,
            openedBy: null,
            shift: null,
            actualShift: null,
            sessionId: null
          });
        }
      } else {
        const data = await response.json();
        
        // El backend devuelve directamente el objeto con isOpened (no data.data)
        const statusData = data.data || data;
        
        setStatus({
          isOpen: statusData.isOpened || statusData.isOpen || false,
          openingAmount: statusData.openingAmount || null,
          openedAt: statusData.openedAt || null,
          openedBy: statusData.openedBy || null,
          shift: statusData.currentShift || statusData.shift || null,
          actualShift: statusData.actualShift || null,
          sessionId: statusData.sessionId || null
        });
      }

    } catch (err) {
      console.error('Error checking cash register status:', err);
      setError(err instanceof Error ? err.message : 'Error al verificar estado de caja');
      
      // Default to closed if we can't determine status
      setStatus({
        isOpen: false,
        openingAmount: null,
        openedAt: null,
        openedBy: null,
        shift: null,
        actualShift: null,
        sessionId: null
      });
    } finally {
      setLoading(false);
    }
  }, [selectedLocation?.id, user?.locationId]);

  useEffect(() => {
    checkStatus();
    
    // Check status every 5 minutes
    const interval = setInterval(checkStatus, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [checkStatus]);

  return {
    status,
    loading,
    error,
    checkStatus
  };
};